import type { AssetType } from '~/types';
import { Button, type ModalProps, Col, Row } from '@workramp/ui';
import { useState } from 'react';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { I18n, getCurrentUser } from '~/common/explicit-exports';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { useCreateContentMutation } from '../../api';
import { PromptModal } from '../../../../../features/common';
import { MAX_NAME_LENGTH } from '../../constants';

export interface CreateContentModalProps extends Omit<ModalProps, 'children' | 'isOpen'> {
  isOpen: boolean;
  type: AssetType;
  itemFolderId?: string;
  initialName?: string;
  onSuccess?: () => void;
  onCreateFromFile: (name: string) => void;
  onCreateFromDeepSearch: (name: string) => void;
}

export function CreateContentModal({
  type,
  itemFolderId,
  initialName = '',
  onSuccess,
  onCreateFromFile,
  onCreateFromDeepSearch,
  onClose,
  ...modalProps
}: CreateContentModalProps) {
  const currentUser = getCurrentUser();
  const contentName = AssetPickerTypes.getDisplayName(type);
  const isMultiFile = isFeatureEnabled('fy26-q2-generate-guide-from-multiple-files');

  const TRANSLATED_CREATE = I18n.t('js.admin.content_creation.create');
  const TRANSLATED_CREATE_FROM_FILE = I18n.t('js.admin.content_creation.create_from_file');
  const TRANSLATED_CREATE_FROM_FILES = I18n.t('js.admin.content_creation.create_from_files');
  const TRANSLATED_CREATE_FROM_DEEP_SEARCH = I18n.t('js.admin.content_creation.create_from_deep_search');
  const TRANSLATED_TITLE = I18n.t('js.admin.content_creation.create_modal_title', {
    content: contentName,
  });
  const TRANSLATED_PLACEHOLDER = I18n.t('js.admin.content_creation.create_modal_placeholder', {
    content: contentName,
  });
  const TRANSLATED_LABEL = I18n.t('js.admin.content_creation.create_modal_label', {
    content: contentName,
  });

  const [name, setName] = useState(initialName);

  const createContentMutation = useCreateContentMutation();

  const typesWithCharacterLimit: AssetType[] = [AssetPickerTypes.GUIDE];
  const hasCharacterLimit = typesWithCharacterLimit.includes(type);

  const handleCreateFromScratchSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }

    createContentMutation.mutate(
      {
        type,
        name: trimmedName,
        itemFolderId,
      },
      {
        onSuccess: (result) => {
          onSuccess?.();

          const redirectUrl =
            AssetPickerTypes.getItemAdminEditUrl(result, null) || AssetPickerTypes.getItemAdminUrl(result, null);
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }

          onClose?.();
        },
      },
    );
  };

  return (
    <PromptModal
      isRequired
      characterLimit={hasCharacterLimit ? MAX_NAME_LENGTH : undefined}
      title={TRANSLATED_TITLE}
      fieldLabel={TRANSLATED_LABEL}
      placeholder={TRANSLATED_PLACEHOLDER}
      value={name}
      onChange={setName}
      confirm={TRANSLATED_CREATE}
      onConfirm={handleCreateFromScratchSubmit}
      onClose={onClose}
      secondaryActions={
        isFeatureEnabled('fy25_q3_ai_create_from_file') &&
        currentUser.enterprise.isAIAssistEnabled &&
        type === AssetPickerTypes.GUIDE && (
          <Row>
            <Col xs="auto">
              <Button
                size="lg"
                variant="text-inline"
                leadingIconName="magic:regular"
                onPress={() => onCreateFromFile(name)}
                isDisabled={isMultiFile && !name.trim()}
              >
                {isMultiFile ? TRANSLATED_CREATE_FROM_FILES : TRANSLATED_CREATE_FROM_FILE}
              </Button>
            </Col>
            {isFeatureEnabled('fy26_q1_deep_search_for_guide_creation') && (
              <Col xs="auto">
                <Button
                  size="lg"
                  variant="text-inline"
                  leadingIconName="search:regular"
                  onPress={() => onCreateFromDeepSearch(name)}
                  isDisabled={isMultiFile && !name.trim()}
                >
                  {TRANSLATED_CREATE_FROM_DEEP_SEARCH}
                </Button>
              </Col>
            )}
          </Row>
        )
      }
      {...modalProps}
    />
  );
}
